import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { LockKeyhole, LogOut } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

/**
 * Trava de tela inteira do Free pós-período-gratuito — decidido em 02/10/2026.
 *
 * Mesma divisão de trabalho do `DesafioMfa`: esta tela é o portão que se VÊ, e
 * não tem como ser dispensada — sair daqui é pagar ou sair da conta. O portão
 * que VALE é o banco (`resolve_plan_limit_key` devolvendo `plan:free_expirado`,
 * zerando o limite de imóvel e contrato); esta tela só evita que a pessoa
 * descubra a trava tentando cadastrar algo e lendo um erro de RLS sem contexto.
 *
 * O preço vem do MESMO campo que todo outro plano usa
 * (`plans.stripe_price_id_monthly`, linha `plan_key = 'free'`) — não existe
 * campo novo, e o checkout é o `create-checkout` de sempre: nenhuma Edge
 * Function nova para isto.
 */
export function TravaPeriodoGratis() {
  const { signOut } = useAuth();
  const [pagando, setPagando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const { data: preco, isLoading: carregandoPreco } = useQuery({
    queryKey: ["free-pos-trial-price"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("plans")
        .select("stripe_price_id_monthly, preco_apos_gratis")
        .eq("plan_key", "free")
        .eq("pricing_version", "v3")
        .eq("active", true)
        .maybeSingle();
      if (error) return null;
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const precoFormatado = preco?.preco_apos_gratis != null
    ? preco.preco_apos_gratis.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : null;

  // Identificador ainda não colado em Superadmin → Planos: o preço real não
  // existe neste ambiente, e tentar checkout devolveria "plano indisponível"
  // sem dizer por quê. Melhor dizer a verdade aqui do que abrir um Stripe que
  // vai recusar.
  const precoConfigurado = !!preco?.stripe_price_id_monthly
    && !preco.stripe_price_id_monthly.startsWith("STRIPE_PRICE_ID");

  async function pagar() {
    if (!preco?.stripe_price_id_monthly) return;
    setErro(null);
    setPagando(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { priceId: preco.stripe_price_id_monthly },
      });
      if (error || !data?.url) {
        setErro("Não foi possível iniciar o pagamento agora. Tente novamente em instantes.");
        return;
      }
      window.open(data.url, "_blank", "noopener,noreferrer");
    } finally {
      setPagando(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md">
        <Card className="border-primary/20 shadow-lg">
          <CardContent className="p-8 text-center space-y-4">
            <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
              <LockKeyhole className="h-7 w-7 text-primary" aria-hidden />
            </div>
            <div>
              <h1 className="text-lg font-bold text-foreground">Seu período gratuito terminou</h1>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                Os 3 meses grátis do plano Free acabaram. Para continuar usando o GIFI
                {precoFormatado ? ` com 1 imóvel, confirme o pagamento de ${precoFormatado} por mês.` : ", confirme o pagamento mensal."}
              </p>
            </div>

            {erro && (
              <p role="alert" className="text-sm text-destructive">{erro}</p>
            )}

            {!carregandoPreco && !precoConfigurado && (
              <p role="status" className="text-sm text-muted-foreground border rounded-md p-3">
                O pagamento ainda está sendo configurado. Tente novamente em breve, ou fale com o
                suporte se isto continuar aparecendo.
              </p>
            )}

            <Button
              className="w-full gap-2"
              disabled={carregandoPreco || !precoConfigurado || pagando}
              onClick={pagar}
            >
              <LockKeyhole className="h-4 w-4" aria-hidden />
              {pagando ? "Abrindo pagamento…" : "Pagar e continuar"}
            </Button>

            <button
              type="button"
              onClick={() => signOut()}
              className="inline-flex items-center justify-center gap-1.5 text-sm text-muted-foreground hover:underline"
            >
              <LogOut className="h-3.5 w-3.5" aria-hidden />
              Sair da conta
            </button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
